import { BulletPointTextarea, Form } from "@/base";
import { useApiServiceContext } from "@/contexts";
import { useDebouncedCallback } from "@/hooks";
import { cn } from "@heroui/react";
import { joiResolver } from "@hookform/resolvers/joi";
import {
    type ApplicantShared,
    ApplicantUpdateDto,
    ApplicantValidationSchemas,
    validationOptions,
} from "@sr-partner-portal/common";
import {
    useMutation,
    useQueryClient,
} from "@tanstack/react-query";
import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";

export type ApplicantUpdateFormParameters = {
    customerNotes: string;
};

interface ApplicantExternalNotesProps {
    applicant: ApplicantShared;
    isDisabled?: boolean;
    companyName?: string;
    shareLinkToken: string;
    isViewOnly?: boolean;
}

const ApplicantExternalNotes = ({
    applicant,
    isDisabled,
    companyName,
    shareLinkToken,
    isViewOnly,
}: ApplicantExternalNotesProps) => {
    const apiService = useApiServiceContext();
    const queryClient = useQueryClient();
    const formController = useForm<ApplicantUpdateFormParameters>({
        resolver: joiResolver(
            ApplicantValidationSchemas.update.full.form,
            validationOptions,
        ),
        defaultValues: {
            customerNotes: applicant.customerNotes || "",
        },
    });

    const mutation = useMutation({
        mutationFn: ({
            applicantUpdateDto,
        }: {
            applicantUpdateDto: ApplicantUpdateDto;
            skipFormReset?: boolean;
        }) => {
            return apiService.applicants.updateByShareLinkTokenAsync(
                shareLinkToken,
                applicantUpdateDto,
            );
        },

        onMutate: async (variables) => {
            // Cancel any outgoing refetches
            // (so they don't overwrite our optimistic update)
            await queryClient.cancelQueries({
                queryKey: ["sharedApplicant", shareLinkToken],
            });

            // Snapshot the previous value
            const previousApplicants = queryClient.getQueryData([
                "sharedApplicant",
                shareLinkToken,
            ]);

            // Optimistically update to the new value
            queryClient.setQueryData(
                ["sharedApplicant", shareLinkToken],
                (old: ApplicantShared) => ({
                    ...old,
                    customerNotes:
                        variables.applicantUpdateDto.customerNotes,
                }),
            );

            // Return a context object with the snapshotted value
            return { previousApplicants };
        },

        // If the mutation fails,
        // use the context returned from onMutate to roll back
        onError: (_error, _variables, context) => {
            queryClient.setQueryData(
                ["sharedApplicant", shareLinkToken],
                context.previousApplicants,
            );
            toast.error("Speichern des Bewerbers fehlgeschlagen");
        },

        onSuccess: (applicantDto, variables) => {
            toast.success(`Bewerber ${applicantDto.name} gespeichert`, {
                id: "ApplicantNotes_success",
            });

            if (variables.skipFormReset) {
                const formValues = formController.getValues();
                formController.reset(formValues);
                return;
            }

            formController.reset({
                customerNotes: applicantDto.customerNotes,
            });
        },

        // Always refetch after error or success:
        onSettled: () => {
            queryClient.invalidateQueries({
                queryKey: ["sharedApplicant", shareLinkToken],
            });
        }
    });

    const onSubmit = useCallback(
        async (
            data: Omit<ApplicantUpdateDto, "convertToDbObject"> & {
                skipFormReset?: boolean;
            },
        ) => {
            if (!formController.formState.isDirty || mutation.isPending) {
                return;
            }

            await mutation.mutate({
                applicantUpdateDto: new ApplicantUpdateDto({
                    customerNotes: data.customerNotes,
                }),
                skipFormReset: data.skipFormReset,
            });
        },
        [formController.formState.isDirty, mutation.mutate, mutation.isPending],
    );

    const saveOnChange = useDebouncedCallback(
        () =>
            formController.handleSubmit((data) =>
                onSubmit({
                    ...data,
                    skipFormReset: true,
                }),
            )(),
        500,
    );

    useEffect(() => {
        formController.reset({
            customerNotes: applicant.customerNotes || "",
        });
    }, [applicant.customerNotes, formController]);

    return (
        <Form controller={formController} onSubmit={onSubmit}>
            <div className="flex h-full flex-col gap-2">
                <div>
                    {companyName ? `${companyName} Notizen` : "Kunden Notizen"}
                </div>
                <BulletPointTextarea
                    fieldName="customerNotes"
                    label="Kunden Notizen"
                    isDisabled={isDisabled || isViewOnly}
                    onBlur={formController.handleSubmit(onSubmit)}
                    onChange={saveOnChange}
                    hideLabel={true}
                    className={cn("h-full min-h-52", isViewOnly && "opacity-70")}
                    wrapperClassName="h-full"
                />
            </div>
        </Form>
    );
};

export { ApplicantExternalNotes };
